import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Plus, Trash2, Lock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Polls() {
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', type: 'poll', options: ['', ''], anonymous: false, multiple_choice: false });
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isManagement = ['admin', 'team_leader'].includes(user.role);

  const fetchPolls = useCallback(async () => {
    try { const res = await axios.get(`${API}/polls`, { headers: getHeaders() }); setPolls(res.data); } catch (e) { toast.error('Failed to load polls'); }
  }, []);

  useEffect(() => { fetchPolls(); }, []);

  const addOption = () => setForm({...form, options: [...form.options, '']});
  const removeOption = (i) => setForm({...form, options: form.options.filter((_, idx) => idx !== i)});
  const updateOption = (i, v) => { const opts = [...form.options]; opts[i] = v; setForm({...form, options: opts}); };

  const handleCreate = async () => {
    if (!form.title || form.options.filter(o => o.trim()).length < 2) { toast.error('Title and at least 2 options required'); return; }
    try {
      await axios.post(`${API}/polls`, {...form, options: form.options.filter(o => o.trim())}, { headers: getHeaders() });
      toast.success('Poll created'); setShowForm(false); setForm({ title: '', description: '', type: 'poll', options: ['', ''], anonymous: false, multiple_choice: false }); fetchPolls();
    } catch (e) { toast.error('Failed'); }
  };

  const handleVote = async (pollId, choice) => {
    try { await axios.post(`${API}/polls/${pollId}/vote`, { choice }, { headers: getHeaders() }); toast.success('Vote recorded'); fetchPolls(); } catch (e) { toast.error('Failed to vote'); }
  };

  const handleClose = async (pollId) => {
    try { await axios.put(`${API}/polls/${pollId}/close`, {}, { headers: getHeaders() }); toast.success('Poll closed'); fetchPolls(); } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (pollId) => {
    try { await axios.delete(`${API}/polls/${pollId}`, { headers: getHeaders() }); toast.success('Deleted'); fetchPolls(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div data-testid="polls-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}><BarChart3 className="inline-block mr-2 mb-1" size={32} />Polls & Surveys</h1>
        <p className="text-slate-500 text-sm">Create polls, gather feedback, and track results</p>
      </div>

      {isManagement && <div className="flex justify-end mb-4"><Button data-testid="create-poll-btn" onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs"><Plus size={14} className="mr-1" />Create Poll</Button></div>}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Create Poll / Survey</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Title</Label><Input data-testid="poll-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="What would you like to ask?" /></div>
            <div><Label className="text-xs font-bold">Description (optional)</Label><Textarea data-testid="poll-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div>
              <Label className="text-xs font-bold mb-1">Options</Label>
              {form.options.map((o, i) => (
                <div key={`option-${i}`} className="flex gap-2 mb-1">
                  <Input data-testid={`poll-option-${i}`} value={o} onChange={e => updateOption(i, e.target.value)} placeholder={`Option ${i + 1}`} className="text-sm" />
                  {form.options.length > 2 && <Button variant="ghost" size="sm" onClick={() => removeOption(i)} className="text-red-400 h-9"><Trash2 size={14} /></Button>}
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={addOption} className="text-blue-600 text-xs"><Plus size={12} className="mr-1" />Add Option</Button>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.anonymous} onChange={e => setForm({...form, anonymous: e.target.checked})} /> Anonymous</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.multiple_choice} onChange={e => setForm({...form, multiple_choice: e.target.checked})} /> Multiple Choice</label>
            </div>
            <Button data-testid="submit-poll-btn" onClick={handleCreate} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Create Poll</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-4">
        {polls.length === 0 ? <p className="text-center text-slate-400 py-12">No polls yet</p> : polls.map(p => {
          const maxVotes = Math.max(...(p.options || []).map(o => o.vote_count), 1);
          return (
            <div key={p.id} data-testid={`poll-${p.id}`} className={`bg-white border rounded-lg shadow-sm p-5 ${p.status === 'closed' ? 'border-slate-300 opacity-80' : 'border-blue-100'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-blue-900">{p.title}</h3>
                    {p.status === 'closed' && <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-bold uppercase"><Lock size={8} className="inline mr-0.5" />Closed</span>}
                  </div>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">by {p.created_by_name} &middot; {p.total_votes} votes{p.anonymous ? ' &middot; Anonymous' : ''}</p>
                </div>
                {isManagement && (
                  <div className="flex gap-1">
                    {p.status === 'active' && <Button size="sm" variant="outline" onClick={() => handleClose(p.id)} className="text-xs h-7"><Lock size={12} className="mr-1" />Close</Button>}
                    {user.role === 'admin' && <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500 h-7"><Trash2 size={12} /></Button>}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {(p.options || []).map((opt) => {
                  const pct = p.total_votes > 0 ? Math.round((opt.vote_count / p.total_votes) * 100) : 0;
                  const isUserChoice = p.user_choice === opt.text;
                  return (
                    <div key={opt.text} data-testid={`poll-opt-${p.id}-${opt.text}`}>
                      {p.status === 'active' && !p.user_voted ? (
                        <button onClick={() => handleVote(p.id, opt.text)} className="w-full text-left px-3 py-2 border border-blue-200 rounded-lg hover:bg-blue-50 text-sm font-medium text-blue-900 transition-colors">{opt.text}</button>
                      ) : (
                        <div className="relative px-3 py-2 rounded-lg overflow-hidden border border-slate-200">
                          <div className="absolute inset-y-0 left-0 bg-blue-100 rounded-lg transition-all" style={{ width: `${pct}%` }} />
                          <div className="relative flex items-center justify-between">
                            <span className={`text-sm font-medium ${isUserChoice ? 'text-blue-800' : 'text-slate-700'}`}>
                              {isUserChoice && <CheckCircle size={12} className="inline mr-1 text-blue-600" />}{opt.text}
                            </span>
                            <span className="text-xs font-bold text-slate-600">{pct}% ({opt.vote_count})</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
